import { useMemo, useRef, useState } from "react";
import { Upload, Image as ImageIcon, Loader2, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fieldLabels = [
  ["receipt_type", "Type"],
  ["recipient_name", "Name"],
  ["recipient_tag", "Chime tag"],
  ["amount", "Amount"],
  ["direction", "Direction"],
  ["memo", "Memo"],
  ["memo_emoji", "Memo emoji"],
  ["memo_emoji_label", "Emoji label"],
  ["memo_emoji_source", "Emoji source"],
  ["date_text", "Date"],
  ["time_text", "Time"],
  ["status", "Status"],
  ["confidence", "Confidence"],
  ["image_sha256", "Image hash"],
];

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return "Not detected";
  if (key === "amount") return `$${Number(value).toFixed(2)}`;
  if (key === "confidence") return `${Math.round(Number(value) * 100)}%`;
  return String(value);
}

function ReceiptReaderPage() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const receipt = result?.receipt || null;
  const rawText = receipt?.raw_text || "";
  const compactJson = useMemo(() => {
    if (!receipt) return "";
    return JSON.stringify(receipt, null, 2);
  }, [receipt]);

  const chooseFile = (nextFile) => {
    setError("");
    setResult(null);
    setFile(nextFile || null);
    setPreview(nextFile ? URL.createObjectURL(nextFile) : "");
  };

  const upload = async () => {
    if (!file) {
      setError("Choose a receipt image first.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/receipt-reader/extract`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.detail || data.error || "Receipt extraction failed.");
      }
      setResult(data);
    } catch (err) {
      setError(err.message || "Receipt extraction failed.");
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async () => {
    if (!compactJson) return;
    await navigator.clipboard.writeText(compactJson);
  };

  return (
    <main className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800 bg-zinc-950/95">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div>
            <p className="text-sm font-semibold text-white">Receipt Reader</p>
            <p className="text-xs text-zinc-500">Chime receipt OCR for payment verification</p>
          </div>
          <a className="text-sm text-zinc-400 hover:text-white" href="/">
            Gateway Console
          </a>
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl gap-6 px-5 py-6 lg:grid-cols-[420px_1fr]">
        <div className="space-y-4">
          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              chooseFile(event.dataTransfer.files?.[0]);
            }}
          >
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex min-h-[220px] w-full flex-col items-center justify-center rounded-md border border-dashed border-zinc-700 bg-zinc-950/70 px-5 text-center hover:border-emerald-500/70"
            >
              <ImageIcon className="h-9 w-9 text-emerald-400" />
              <span className="mt-3 text-sm font-medium text-white">Upload receipt image</span>
              <span className="mt-1 text-xs text-zinc-500">Drop image here or click to choose</span>
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => chooseFile(event.target.files?.[0])}
            />

            {preview ? (
              <img
                src={preview}
                alt="Receipt preview"
                className="mt-4 max-h-[540px] w-full rounded-md border border-zinc-800 object-contain"
              />
            ) : null}

            <button
              type="button"
              onClick={upload}
              disabled={loading}
              className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-emerald-500 px-4 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Read receipt
            </button>
          </div>

          {error ? (
            <div className="flex gap-3 rounded-lg border border-red-900/70 bg-red-950/40 p-4 text-sm text-red-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-white">Extracted receipt</h1>
                <p className="text-xs text-zinc-500">Review fields before using them for approval decisions.</p>
              </div>
              {receipt ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : null}
            </div>

            {receipt ? (
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {fieldLabels.map(([key, label]) => (
                  <div key={key} className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
                    <p className="mt-1 break-words text-sm font-medium text-zinc-100">
                      {formatValue(key, receipt[key])}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950/50 p-5 text-sm text-zinc-500">
                Upload one of the Chime receipt screenshots to extract amount, tag, date, status, and hash.
              </div>
            )}
          </div>

          {receipt?.history?.length ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
              <h2 className="text-sm font-semibold text-white">History rows</h2>
              <div className="mt-3 overflow-hidden rounded-md border border-zinc-800">
                {receipt.history.map((item, index) => (
                  <div key={`${item.direction}-${item.amount}-${item.date}-${index}`} className="grid grid-cols-4 gap-2 border-b border-zinc-800 px-3 py-2 text-sm last:border-b-0">
                    <span className="text-zinc-300">{item.direction}</span>
                    <span className="text-zinc-400">{item.name || "-"}</span>
                    <span className="font-medium text-zinc-100">${Number(item.amount).toFixed(2)}</span>
                    <span className="text-zinc-400">{item.date || "-"}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {receipt ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-white">JSON</h2>
                <button
                  type="button"
                  onClick={copyJson}
                  className="inline-flex items-center gap-2 rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 hover:border-zinc-500"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
              </div>
              <pre className="mt-3 max-h-72 overflow-auto rounded-md bg-zinc-950 p-3 text-xs text-zinc-300">
                {compactJson}
              </pre>
            </div>
          ) : null}

          {rawText ? (
            <details className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-white">Raw OCR text</summary>
              <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-zinc-950 p-3 text-xs text-zinc-400">
                {rawText}
              </pre>
            </details>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export default ReceiptReaderPage;
